import { Input } from "@volca/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import type {
  SpreadsheetColumnFilter,
  SpreadsheetFilterCondition,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-region-view";
import { SECTION_LABEL_CLASSNAME } from "./constants";

interface ConditionFilterEditorProps {
  filter: SpreadsheetColumnFilter;
  onChange: (filter: SpreadsheetColumnFilter) => void;
}

const NO_CONDITION = "none";

const CONDITION_OPTIONS: {
  value: SpreadsheetFilterCondition | typeof NO_CONDITION;
  label: string;
}[] = [
  { value: NO_CONDITION, label: "No condition" },
  { value: "contains", label: "Contains" },
  { value: "equals", label: "Equals" },
  { value: "greater_than", label: "Greater than" },
  { value: "less_than", label: "Less than" },
];

const isFilterCondition = (
  value: string | null,
): value is SpreadsheetFilterCondition =>
  CONDITION_OPTIONS.some(
    (option) => option.value !== NO_CONDITION && option.value === value,
  );

export const ConditionFilterEditor = ({
  filter,
  onChange,
}: ConditionFilterEditorProps) => (
  <>
    <label
      htmlFor="spreadsheet-filter-condition"
      className={SECTION_LABEL_CLASSNAME}
    >
      Keep rows where this column is
    </label>
    <div className="w-full">
      <Select
        value={filter.condition ?? NO_CONDITION}
        items={CONDITION_OPTIONS}
        onValueChange={(value) => {
          const condition = isFilterCondition(value) ? value : null;
          onChange({
            ...filter,
            condition,
            conditionValue: condition === null ? "" : filter.conditionValue,
          });
        }}
      >
        <SelectTrigger id="spreadsheet-filter-condition" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {CONDITION_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    {filter.condition && (
      <div className="w-full">
        <Input
          aria-label="Condition value"
          placeholder="Value"
          value={filter.conditionValue}
          onChange={(event) =>
            onChange({ ...filter, conditionValue: event.target.value })
          }
        />
      </div>
    )}
  </>
);
